import apiClient from "./apiClient";
import {
  Suggestion,
  UserPreferences,
  SuggestionFilters,
  SuggestionSortOptions,
  CreateSuggestionData,
  UpdateSuggestionData,
} from "../types/suggestion.types";

export const suggestionApi = {
  // Get all suggestions with optional filters and sorting
  getSuggestions: async (
    filters?: SuggestionFilters,
    sort?: SuggestionSortOptions,
  ) => {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.category) params.append("category", filters.category);
      if (filters.tags) params.append("tags", filters.tags.join(","));
      if (filters.dateRange) {
        params.append("startDate", filters.dateRange.start);
        params.append("endDate", filters.dateRange.end);
      }
      if (filters.location) {
        params.append("lat", filters.location.lat.toString());
        params.append("lng", filters.location.lng.toString());
        params.append("radius", filters.location.radius.toString());
      }
      if (filters.minConfidence)
        params.append("minConfidence", filters.minConfidence.toString());
      if (filters.source) params.append("source", filters.source);
    }

    if (sort) {
      params.append("sortField", sort.field);
      params.append("sortDirection", sort.direction);
    }

    const response = await apiClient.api.get<Suggestion[]>(
      `/suggestions?${params.toString()}`,
    );
    return response.data;
  },

  // Get a single suggestion by ID
  getSuggestion: async (id: string) => {
    const response = await apiClient.api.get<Suggestion>(`/suggestions/${id}`);
    return response.data;
  },

  // Create a new suggestion
  createSuggestion: async (data: CreateSuggestionData) => {
    const response = await apiClient.api.post<Suggestion>("/suggestions", data);
    return response.data;
  },

  // Update an existing suggestion
  updateSuggestion: async (id: string, data: UpdateSuggestionData) => {
    const response = await apiClient.api.patch<Suggestion>(`/suggestions/${id}`, data);
    return response.data;
  },

  // Delete a suggestion
  deleteSuggestion: async (id: string) => {
    await apiClient.api.delete(`/suggestions/${id}`);
  },

  // Get user preferences
  getUserPreferences: async () => {
    const response = await apiClient.api.get<UserPreferences>("/suggestions/preferences");
    return response.data;
  },

  // Update user preferences
  updateUserPreferences: async (preferences: Partial<UserPreferences>) => {
    const response = await apiClient.api.patch<UserPreferences>(
      "/suggestions/preferences",
      preferences,
    );
    return response.data;
  },

  // Generate new suggestions based on preferences
  generateSuggestions: async () => {
    const response = await apiClient.api.post<Suggestion[]>("/suggestions/generate");
    return response.data;
  },

  // Get suggestion categories
  getCategories: async () => {
    const response = await apiClient.api.get<string[]>("/suggestions/categories");
    return response.data;
  },

  // Get popular tags
  getTags: async () => {
    const response = await apiClient.api.get<string[]>("/suggestions/tags");
    return response.data;
  },

  // Search for places
  searchPlaces: async (params: {
    query?: string;
    latitude: number;
    longitude: number;
    radius?: number;
    type?: string;
    categories?: string;
    minprice?: number;
    maxprice?: number;
  }) => {
    const queryParams = new URLSearchParams();

    if (params.query) queryParams.append("query", params.query);
    queryParams.append("latitude", params.latitude.toString());
    queryParams.append("longitude", params.longitude.toString());
    if (params.radius) queryParams.append("radius", params.radius.toString());
    // Support both 'type' and 'categories' parameters
    if (params.categories) {
      queryParams.append("categories", params.categories);
    } else if (params.type) {
      queryParams.append("type", params.type);
    }
    if (params.minprice)
      queryParams.append("minprice", params.minprice.toString());
    if (params.maxprice)
      queryParams.append("maxprice", params.maxprice.toString());

    const response = await apiClient.api.get(
      `/v1/suggestions/places/search?${queryParams.toString()}`,
    );
    return response.data.data || response.data;
  },

  // Get address autocomplete suggestions
  getAddressAutocomplete: async (
    input: string,
    sessionToken?: string,
    country?: string,
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append("input", input);
    if (sessionToken) queryParams.append("sessiontoken", sessionToken);

    // Try to detect user's country from browser if not provided
    if (!country) {
      // Use browser's language/locale to infer country (fallback)
      const locale = navigator.language || navigator.languages?.[0];
      if (locale && locale.includes("-")) {
        // Extract country code from locale (e.g., 'en-US' -> 'US')
        country = locale.split("-")[1];
      }
    }

    if (country) queryParams.append("country", country);

    const response = await apiClient.api.get(
      `/v1/suggestions/autocomplete?${queryParams.toString()}`,
    );
    return response.data.data || response.data;
  },

  // Geocode an address to get coordinates
  geocodeAddress: async (address: string) => {
    const queryParams = new URLSearchParams();
    queryParams.append("address", address);

    const response = await apiClient.api.get(
      `/v1/suggestions/geocode?${queryParams.toString()}`,
    );
    return response.data.data || response.data;
  },
};
